/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { isSearchable } from '../../../utils/validations'
import useId from '../../hooks/useId'
import {
  SearchBarBackButton,
  SearchBarClearButton,
  SearchBarContent,
  SearchBarInput,
  SearchBarSide,
  SearchBarWrapper
} from './SearchBar.styles'
import { SearchBarProps } from './SearchBar.types'

let searchTimer: NodeJS.Timeout

const SearchBar = ({
  id = 'Search',
  className = '',
  defaultValue = '',
  placeholder = 'Buscar...',
  autoFocus,
  backButton,
  clearButton,
  searchTimeout = 1200,
  searchTermMinLength = 1,
  searchActions,
  styles,
  'data-testid': testid = 'search-bar'
}: SearchBarProps) => {
  const searchInput = useRef<any>(null)
  const [searchTerm, setSearchTerm] = useState<string>(defaultValue)
  const { getTestId } = useId(testid)

  const observer = useMemo(
    () =>
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const { isIntersecting } = entry
          const target = entry.target as HTMLElement

          if (isIntersecting && autoFocus) {
            target.focus()
          }
        })
      }),
    [autoFocus]
  )

  const handleInput = ({ target: { value } }: any) => {
    setSearchTerm(value)
    clearTimeout(searchTimer)
    const canSearch = isSearchable(value, searchTermMinLength)

    if (canSearch && searchActions && searchTimeout) {
      searchTimer = setTimeout(() => {
        searchActions({ searchTerm: value })
      }, searchTimeout)
    }

    if (!canSearch && clearButton?.actions) {
      clearButton.actions({ searchTerm })
    }
  }

  const handleKey = ({ keyCode }: any) => {
    if (keyCode === 13) {
      searchInput.current.blur()

      if (isSearchable(searchTerm, searchTermMinLength) && searchActions) {
        clearTimeout(searchTimer)
        searchActions({ searchTerm })
      }
    }
  }

  const handleClear = () => {
    setSearchTerm('')
    if (clearButton?.actions) {
      clearButton.actions({ searchTerm })
    }
    clearTimeout(searchTimer)
  }

  useEffect(() => {
    observer.observe(searchInput.current)
  }, [observer])

  return (
    <SearchBarWrapper
      id={id}
      className={`SearchBar ${className}`}
      sx={styles}
      data-testid={getTestId()}>
      {!!backButton && (
        <SearchBarSide
          id={`${id}-side-start`}
          className="SearchBarSide SideStart">
          <SearchBarBackButton
            id={`${id}-back`}
            className="SearchBarBack"
            onClick={backButton?.actions}
            data-testid={getTestId('back')}
            {...backButton}
          />
        </SearchBarSide>
      )}
      <SearchBarContent id={`${id}-content`} className="SearchBarContent">
        <SearchBarInput
          id={`${id}-input`}
          className="SearchBarInput"
          name="search"
          value={searchTerm}
          placeholder={placeholder}
          inputMode="search"
          onInput={handleInput}
          onKeyUp={handleKey}
          data-testid={getTestId('input')}
          inputProps={{
            ref: searchInput,
            inputMode: 'search'
          }}
        />
      </SearchBarContent>
      {!!clearButton && searchTerm.length > 0 && (
        <SearchBarSide id={`${id}-side-end`} className="SearchBarSide SideEnd">
          <SearchBarClearButton
            id={`${id}-clear`}
            className="SearchBarClear"
            onClick={handleClear}
            data-testid={getTestId('clear')}
            {...clearButton}
          />
        </SearchBarSide>
      )}
    </SearchBarWrapper>
  )
}

export default SearchBar
